import { useState, useCallback } from 'react';
import { EntityType } from '../config/uploads';
import { UploadState, UploadResult, UploadProgress } from '../types/uploads';
import { createMediaUploader } from '../utils/mediaUploader';
import { useAuthenticatedApi } from './useAuthenticatedApi';

export const useMediaUpload = () => {
  const { callApi } = useAuthenticatedApi();
  const [uploadState, setUploadState] = useState<UploadState>({
    isUploading: false,
    progress: null,
    error: null,
    completed: false,
  });

  const resetState = useCallback(() => {
    setUploadState({
      isUploading: false,
      progress: null,
      error: null,
      completed: false,
    });
  }, []);

  const upload = useCallback(async (
    file: File,
    entityType: EntityType,
    entityId: string
  ): Promise<UploadResult> => {
    resetState();
    setUploadState(prev => ({ ...prev, isUploading: true }));

    try {
      const uploader = createMediaUploader(callApi);
      
      const result = await uploader.uploadFile(
        file,
        entityType,
        entityId,
        (progress: UploadProgress) => {
          setUploadState(prev => ({ ...prev, progress }));
        }
      );

      setUploadState(prev => ({ 
        ...prev, 
        isUploading: false, 
        completed: true 
      }));

      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Upload failed';
      setUploadState(prev => ({ 
        ...prev, 
        isUploading: false, 
        error: errorMessage 
      }));
      throw error;
    }
  }, [callApi, resetState]);

  const deleteFile = useCallback(async (key: string): Promise<void> => {
    try {
      const uploader = createMediaUploader(callApi);
      await uploader.deleteFile(key);
    } catch (error) {
      console.error('Delete failed:', error);
      throw error;
    }
  }, [callApi]);

  return {
    upload,
    deleteFile,
    resetState,
    isUploading: uploadState.isUploading,
    progress: uploadState.progress,
    error: uploadState.error,
    completed: uploadState.completed,
  };
}; 